import {
  BillingBusinessLicense,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { isEqual } from 'lodash';
import { ResolvedPlanDetails } from './hooks/useSelectedPlanResolution';

export type UnifiedPlan = {
  id: string;
  name: string;
  price: {
    amount: number;
    currency: string;
    symbol: string;
  };
  ui_config: any;
  config: any;
};

const maybeFormatValueAsNumber = (value: unknown) => {
  if (isNaN(Number(value))) {
    return String(value);
  } else {
    return Number(value).toLocaleString();
  }
};

const getValueForKey = (
  key: string,
  source: Record<string, unknown>
): string => {
  const parts = key.split('.');
  let value: any = source;

  for (const part of parts) {
    value = value?.[part];
  }

  return maybeFormatValueAsNumber(value);
};

/*
    The backend-driven billing CMS allows for interpolation in strings that are shown to the user. For example:
        "{limits.max_users} users included"
    The interpolatable values are anything defined under `config` on a plan (or unified plan from below).
*/
export const interpolateValues = (str: string, plan?: UnifiedPlan) => {
  const regex = /\{(.+?)\}/g;

  if (!plan) {
    return str;
  }

  if (regex.test(str)) {
    return str.replace(regex, (match, key) => {
      return getValueForKey(key, plan.config);
    });
  }

  return str;
};

/*
    Takes in slightly different representations of plans, and returns a unified structure for easier
    consumption. plan represents a license template, or an available, published plan. currentPlan is
    the currently active plan, which could be legacy or have a match with an existing published template.
    If a plan is provided, it will be preferred. 
*/
export const getUnifiedPlan = (
  plan?: BillingLicensePlans['license_templates'][number],
  currentPlan?: BillingBusinessLicense[number]
): UnifiedPlan | undefined => {
  if (!plan && !currentPlan) {
    return undefined;
  }

  if (plan) {
    return {
      id: plan.id,
      name: plan.published_name,
      price: plan.price,
      ui_config: plan.ui_config,
      config: plan.config,
    };
  }

  if (currentPlan) {
    return {
      id: currentPlan.license_plan.id,
      name: currentPlan.license_plan.published_name,
      price: currentPlan.price,
      ui_config: currentPlan.ui_config,
      config: currentPlan.config,
    };
  }

  return {
    id: '',
    name: '',
    price: {
      amount: 0,
      currency: '',
      symbol: '',
    },
    ui_config: {},
    config: {},
  };
};

export const isConfigSectionEqual = (
  comparisonPlanConfigSectionArg?: Record<string, unknown>,
  existingPlanConfigSectionArg?: Record<string, unknown>
) => {
  const { max_users: _, ...comparisonPlanConfigSection } =
    comparisonPlanConfigSectionArg || {};

  const { max_users: __, ...existingPlanConfigSection } =
    existingPlanConfigSectionArg || {};

  return isEqual(comparisonPlanConfigSection, existingPlanConfigSection);
};

/*
    Plans are considered equal if all of the following are true:
        1. They have the same plan category (i.e. general, automation, marketing)
        2. They have the same price and currency
        3. They have the same exact limits
        4. They have the same overage unit size and price

    Note: The backend does not enforce uniqueness across all these traits. That means
    it's possible for multiple plans to show as "equal" to each other. There's no good solution
    for this on the frontend, so if this behavior is encountered it means there are two
    functionally identical plans in the backend, which shouldn't happen and points to a config error.
*/
export const arePlansEqual = (
  comparisonPlan?:
    | BillingLicensePlans['license_templates'][number]
    | ResolvedPlanDetails,
  existingPlan?: BillingBusinessLicense[number]
) => {
  if (!existingPlan) {
    return false;
  }

  if (!comparisonPlan) {
    return false;
  }

  if (
    comparisonPlan.plan_category !== existingPlan.license_plan.plan_category
  ) {
    return false;
  }

  if (
    comparisonPlan.price?.amount !== existingPlan.price.amount ||
    comparisonPlan.price.currency !== existingPlan.price.currency
  ) {
    return false;
  }

  const limitsEqual = isConfigSectionEqual(
    comparisonPlan.config?.limits as Record<string, unknown>,
    existingPlan.config.limits as Record<string, unknown>
  );

  const overageEqual = isConfigSectionEqual(
    comparisonPlan.config?.overage as Record<string, unknown>,
    existingPlan.config.overage as Record<string, unknown>
  );

  return limitsEqual && overageEqual;
};
